import { useUpdateDataset } from '@/queries/datasets';
import type { DatasetDetail } from '@/queries/datasets';
import { getErrorMessage } from '@/utils';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import React, { useCallback, useMemo } from 'react';

import {
  ActionButtonsContainer,
  FormContainer,
  FormField,
} from './EditDatasetForm.styles';

const { TextArea } = Input;

export interface EditDatasetFormProps {
  dataset: DatasetDetail;
  teamId: string;
  onSuccess?: (updatedDataset: DatasetDetail) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
  readOnlyFields?: ('name' | 'description')[];
  showActions?: boolean;
  className?: string;
}

interface FormValues {
  name: string;
  description: string;
}

export const EditDatasetForm: React.FC<EditDatasetFormProps> = ({
  dataset,
  teamId: _teamId,
  onSuccess,
  onCancel,
  onError,
  readOnlyFields = [],
  showActions = true,
  className,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const updateDatasetMutation = useUpdateDataset();

  const initialValues = useMemo(() => ({
    name: dataset.name,
    description: dataset.description || '',
  }), [dataset.name, dataset.description]);

  const handleFinish = useCallback(async (values: FormValues) => {
    try {
      const updateData = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
      };

      const updatedDataset = await updateDatasetMutation.mutateAsync({
        datasetId: dataset.datasetId,
        data: updateData,
      });

      void message.success('Dataset updated successfully');
      onSuccess?.(updatedDataset);
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to update dataset');
      onError?.(errorMessage);
    }
  }, [updateDatasetMutation, dataset.datasetId, message, onSuccess, onError]);

  const handleCancel = useCallback(() => {
    form.setFieldsValue(initialValues);
    onCancel?.();
  }, [form, initialValues, onCancel]);

  const isNameReadOnly = readOnlyFields.includes('name');
  const isDescriptionReadOnly = readOnlyFields.includes('description');
  const isLoading = updateDatasetMutation.isPending;

  return (
    <FormContainer className={className}>
      <Form 
        form={form} 
        layout="vertical" 
        disabled={isLoading}
        initialValues={initialValues}
        onFinish={handleFinish}
      >
        <FormField>
          <Form.Item
            label="Dataset Name"
            name="name"
            rules={isNameReadOnly ? [] : [
              { required: true, message: 'Dataset name is required' },
              { max: 255, message: 'Dataset name must be 255 characters or less' },
            ]}
          >
            <Input
              placeholder="Enter dataset name"
              readOnly={isNameReadOnly}
              maxLength={255}
            />
          </Form.Item>
        </FormField>

        <FormField>
          <Form.Item
            label="Description"
            name="description"
            rules={isDescriptionReadOnly ? [] : [
              { max: 1000, message: 'Description must be 1000 characters or less' }
            ]}
          >
            <TextArea
              placeholder="Enter an optional description for this dataset"
              readOnly={isDescriptionReadOnly}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </FormField>

        {showActions && (
          <ActionButtonsContainer>
            <Button 
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
              loading={isLoading}
            >
              Save Changes
            </Button>
          </ActionButtonsContainer>
        )}
      </Form>
    </FormContainer>
  );
};

export default EditDatasetForm;